import {
  ChargeItem,
  ChargeItemDefinition,
  Invoice,
  ServiceRequest,
  Patient,
  Extension,
} from '@medplum/fhirtypes';
import { MedplumClient, getReferenceString } from '@medplum/core';
import { ARKON } from '@consts/systems.consts';
import { PaymentSource, PaymentStatus } from '../lims-types';
import {
  DEFAULT_CURRENCY,
  DEFAULT_PAYMENT_SOURCE,
  PAYMENT_SOURCE_REGISTRY,
  resolvePaymentSource,
} from '../constants/payment.consts';

export function buildExamChargeItem(
  serviceRequest: ServiceRequest,
  patient: Patient,
  amount: number,
  source: PaymentSource,
  testCode?: string,
  testDisplay?: string,
  discount: number = 0
): ChargeItem {
  return {
    resourceType: 'ChargeItem',
    status: 'billable',
    code: {
      coding: [
        {
          system: ARKON.CS.CHARGE_ITEM.BASE,
          code: ARKON.CS.CHARGE_ITEM.CODES.EXAM_CHARGE,
          display: testDisplay || 'Exam Charge',
        },
        ...(testCode
          ? [
              {
                system: ARKON.CS.GENETIC_TESTS.BASE,
                code: testCode,
                display: testDisplay,
              },
            ]
          : []),
      ],
    },
    subject: { reference: getReferenceString(patient) },
    service: [{ reference: getReferenceString(serviceRequest) }],
    quantity: { value: 1 },
    priceOverride: {
      value: Math.max(0, amount - discount),
      currency: DEFAULT_CURRENCY,
    },
    extension: [
      {
        url: ARKON.SD.PAYMENT_SOURCE,
        valueCode: source,
      },
      ...(discount > 0
        ? [
            {
              url: ARKON.SD.DISCOUNT,
              valueDecimal: discount,
            },
          ]
        : []),
    ],
  };
}

export function buildDepositChargeItem(
  mainChargeItem: ChargeItem,
  patient: Patient,
  amount: number
): ChargeItem {
  return {
    resourceType: 'ChargeItem',
    status: 'billed',
    code: {
      coding: [
        {
          system: ARKON.CS.CHARGE_ITEM.BASE,
          code: ARKON.CS.CHARGE_ITEM.CODES.DEPOSIT,
          display: 'Deposit',
        },
      ],
    },
    subject: { reference: getReferenceString(patient) },
    partOf: [{ reference: getReferenceString(mainChargeItem) }],
    service: mainChargeItem.service, // Link to same ServiceRequest as main charge
    quantity: { value: 1 },
    priceOverride: {
      value: amount,
      currency: DEFAULT_CURRENCY,
    },
  };
}

// ---------------------------------------------------------------------------
// Invoice helpers
// ---------------------------------------------------------------------------

export function buildInvoiceForOrder(
  serviceRequest: ServiceRequest,
  patient: Patient,
  chargeItems: ChargeItem[],
  totalGross: number,
  totalNet: number,
  paymentSource?: PaymentSource
): Invoice {
  const invoice: Invoice = {
    resourceType: 'Invoice',
    status: 'draft', // Both callers currently override this
    subject: { reference: getReferenceString(patient) },
    recipient: { reference: getReferenceString(patient) },
    date: new Date().toISOString(),
    lineItem: chargeItems.map((ci) => ({
      chargeItemReference: { reference: getReferenceString(ci) },
    })),
    totalGross: { value: totalGross, currency: DEFAULT_CURRENCY },
    totalNet: { value: totalNet, currency: DEFAULT_CURRENCY },
    extension: [
      ...(paymentSource
        ? [
            {
              url: ARKON.SD.PAYMENT_SOURCE,
              valueCode: paymentSource,
            },
          ]
        : []),
      {
        url: ARKON.SD.LINKED_ORDER,
        valueReference: { reference: getReferenceString(serviceRequest) },
      },
    ],
  };

  return invoice;
}

// ---------------------------------------------------------------------------
// Extension getters / setters
// ---------------------------------------------------------------------------

export function getPaymentSource(resource: {
  extension?: Extension[];
}): PaymentSource | undefined {
  const ext = resource.extension?.find(
    (e) => e.url === ARKON.SD.PAYMENT_SOURCE
  );
  return resolvePaymentSource(ext?.valueCode);
}

export function getLinkedOrderRef(invoice: Invoice): string | undefined {
  const ext = invoice.extension?.find((e) => e.url === ARKON.SD.LINKED_ORDER);
  return ext?.valueReference?.reference;
}

export function getDiscount(resource: { extension?: Extension[] }): number {
  const ext = resource.extension?.find((e) => e.url === ARKON.SD.DISCOUNT);
  return ext?.valueDecimal || 0;
}

export function setPaymentSourceExtension(
  invoice: Invoice,
  source: PaymentSource
): Invoice {
  const extensions = (invoice.extension || []).filter(
    (e) => e.url !== ARKON.SD.PAYMENT_SOURCE
  );
  extensions.push({ url: ARKON.SD.PAYMENT_SOURCE, valueCode: source });
  return { ...invoice, extension: extensions };
}

export function getInvoiceStatus(invoice: Invoice): PaymentStatus {
  return (invoice.status as PaymentStatus) || 'draft';
}

// ---------------------------------------------------------------------------
// Amount helpers
// ---------------------------------------------------------------------------

export function getDepositTotal(depositItems: ChargeItem[]): number {
  return depositItems.reduce(
    (sum, item) => sum + (item.priceOverride?.value || 0),
    0
  );
}

export function getRemainingBalance(
  totalNet: number,
  depositItems: ChargeItem[]
): number {
  return Math.max(0, totalNet - getDepositTotal(depositItems));
}

export function addPaymentNote(invoice: Invoice, text: string): Invoice {
  const note = {
    time: new Date().toISOString(),
    text,
  };
  return {
    ...invoice,
    note: [...(invoice.note || []), note],
  };
}

// ---------------------------------------------------------------------------
// FHIR search / fetch helpers
// ---------------------------------------------------------------------------

export async function findInvoiceForOrder(
  medplum: MedplumClient,
  serviceRequestId: string,
  patientId?: string
): Promise<Invoice | undefined> {
  const searchParams: Record<string, string> = {
    _count: '100', // Note: silent truncation ceiling. A patient with >100 invoices over time could have the relevant one dropped. Consider a FHIR extension search param if Medplum supports it.
    _sort: '-_lastUpdated',
  };
  if (patientId) {
    searchParams['subject'] = `Patient/${patientId}`;
  }

  const invoices = await medplum.searchResources('Invoice', searchParams);

  return invoices.find((inv) => {
    const ref = getLinkedOrderRef(inv);
    return ref === `ServiceRequest/${serviceRequestId}`;
  });
}

export async function findChargeItemsForOrder(
  medplum: MedplumClient,
  serviceRequestId: string
): Promise<{ mainItems: ChargeItem[]; depositItems: ChargeItem[] }> {
  const chargeItems = await medplum.searchResources('ChargeItem', {
    service: `ServiceRequest/${serviceRequestId}`,
    _count: '200', // prevent silent truncation on orders with many amendments
  });

  const mainItems: ChargeItem[] = [];
  const depositItems: ChargeItem[] = [];

  for (const ci of chargeItems) {
    const isDeposit = ci.code?.coding?.some(
      (c) =>
        c.system === ARKON.CS.CHARGE_ITEM.BASE &&
        c.code === ARKON.CS.CHARGE_ITEM.CODES.DEPOSIT
    );
    if (isDeposit) {
      depositItems.push(ci);
    } else {
      mainItems.push(ci);
    }
  }

  return { mainItems, depositItems };
}

// ---------------------------------------------------------------------------
// ChargeItemDefinition helpers
// ---------------------------------------------------------------------------

export async function fetchChargeItemDefinition(
  medplum: MedplumClient,
  serviceRequest: ServiceRequest
): Promise<ChargeItemDefinition | undefined> {
  const targetSystem = ARKON.CS.GENETIC_TESTS.BASE;

  // Find the coding that matches our target system
  const targetCoding = serviceRequest.code?.coding?.find(
    (c) => c.system === targetSystem
  );
  const code = targetCoding?.code || serviceRequest.code?.coding?.[0]?.code;

  if (!code) {
    return undefined;
  }

  // Use URL search parameter as 'code' parameter is not supported
  const definitionUrl = `${ARKON.BASE}/fhir/ChargeItemDefinition/${code}`;

  const definitions = await medplum.searchResources('ChargeItemDefinition', {
    url: definitionUrl,
    _count: '1',
  });

  return definitions?.[0];
}

export interface PricingOptionSplit {
  source: PaymentSource;
  amount: number;
  discount?: number;
  currency: string;
}

export interface PricingOption {
  label: string;
  totalAmount: number;
  currency: string;
  splits: PricingOptionSplit[];
}

export function getPricesFromDefinition(
  definition: ChargeItemDefinition,
  t?: (key: string, options?: Record<string, unknown>) => string
): PricingOption[] {
  const groups = definition.propertyGroup || [];
  const options: PricingOption[] = [];

  groups.forEach((group) => {
    const components = group.priceComponent || [];
    if (components.length === 0) {
      return;
    }

    const splits: PricingOptionSplit[] = [];
    let totalAmount = 0;
    let currency = DEFAULT_CURRENCY;
    const labelParts: string[] = [];

    components.forEach((comp) => {
      const amount = comp.amount?.value || 0;
      const compCurrency = comp.amount?.currency || DEFAULT_CURRENCY;
      const billing = comp.code?.coding?.[0];
      const code = billing?.code || 'UNKNOWN';

      if (splits.length === 0 && labelParts.length === 0) {
        currency = compCurrency as any; // Take first currency
      }

      const source = resolvePaymentSource(code);

      if (amount >= 0) {
        const resolvedSource = source ?? DEFAULT_PAYMENT_SOURCE;
        splits.push({
          source: resolvedSource,
          amount,
          discount: 0,
          currency: compCurrency,
        });
        totalAmount += amount;
        // Resolve the i18n key from the registry to get the display name at render time
        const displayName = source
          ? t
            ? t(PAYMENT_SOURCE_REGISTRY[source].i18nKey, { ns: 'lims' })
            : PAYMENT_SOURCE_REGISTRY[source].i18nKey
          : billing?.display || code;
        labelParts.push(`${amount} ${compCurrency} (${displayName})`);
      } else {
        // Zero-amount: covered by this billing category — show in label only
        // Prefer the coding display name, then fall back to the raw code
        const displayName = billing?.display || code;
        labelParts.push(displayName);
      }
    });

    options.push({
      label: labelParts.join(' + '),
      totalAmount,
      currency,
      splits,
    });
  });

  return options;
}
